import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Container,
  Dropdown,
  Nav,
  Navbar,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import { useTranslation } from 'react-i18next';
import { SidenavItem } from './SidenavItem';
import { SidenavItemType } from './SidenavItemTypes';
import SidenavItems from './SidenavItems';
import {
  checkRole,
  SiteClaim,
  updateActiveSiteAsync,
} from '../../../features/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { getSiteDetailsAsync } from '../../../features/offices/OfficesSlice';
import { LOCALES, ROLES } from '../../constants';
import FlagEn from '../../../assets/img/lang-en-square.webp';
import FlagFi from '../../../assets/img/lang-fi-square.webp';
import { getEnvironmentInfo } from '../../environmentInfo';

interface SidenavProps {
  HandleSidenavOpen: (IsOpen: boolean) => void;
  Initials: string;
  LogoutUrl: string;
  Username: string;
  SiteClaims: SiteClaim[];
  ActiveSite: SiteClaim | null;
}

const Sidenav: React.FC<SidenavProps> = ({
  HandleSidenavOpen,
  Initials,
  LogoutUrl,
  Username,
  SiteClaims,
  ActiveSite,
}) => {
  const isTravelAgencyOnlyUser = useAppSelector(
    checkRole([ROLES.TravelAgency]),
  );
  const { t, i18n } = useTranslation(['sidenav']);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isSidenavOpen, setIsSidenavOpen] = useState<boolean>(false);
  const [showWintime, setShowWintime] = useState<boolean>(false);

  const changeLanguage = (locale: LOCALES) => {
    // Convert LOCALES enum values to i18next language codes
    const langCode = locale === LOCALES.Fi ? 'fi' : 'en';
    i18n.changeLanguage(langCode);
  };

  const DashboardItem: SidenavItemType = {
    Icon: 'house-door',
    Name: t('sidenav:linkName'),
    Url: '/',
  };

  const initFetch = useCallback(() => {
    if (!ActiveSite?.id) {
      setShowWintime(false);
      return Promise.resolve(null);
    }

    return dispatch(getSiteDetailsAsync(ActiveSite.id))
      .then(response => {
        // Handle your data here, e.g. setState(response)
        if (
          response?.payload?.wintimePurchaseVoucherType !== null &&
          response?.payload?.wintimePurchaseVoucherType === 'OLR'
        ) {
          setShowWintime(true);
        } else {
          setShowWintime(false);
        }
      })
      .catch(() => {
        // Handle errors here, e.g. show toast/notification
        setShowWintime(false);
      });
  }, [ActiveSite?.id, dispatch]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);

  const handleTogglerClick = () => {
    const newIsSidenavOpenValue = !isSidenavOpen;
    setIsSidenavOpen(newIsSidenavOpenValue);
    HandleSidenavOpen(newIsSidenavOpenValue);
  };

  const handleCloseSubmenu = () => {
    setIsSidenavOpen(false);
    HandleSidenavOpen(false);
  };

  const handleSiteClick = (siteId: string, siteName: string) => {
    dispatch(updateActiveSiteAsync({ id: siteId, name: siteName })).then(() => {
      navigate('/');
      window.location.reload();
    });
  };

  const [isNarrow, setIsNarrow] = useState(() => {
    return localStorage.getItem('isNarrow') === 'true';
  });

  useEffect(() => {
    if (isNarrow) {
      document.body.classList.add('sidenav-narrow');
    } else {
      document.body.classList.remove('sidenav-narrow');
    }
  }, [isNarrow]);

  const toggleSidebarWidth = () => {
    const newValue = !isNarrow;
    setIsNarrow(newValue);
    localStorage.setItem('isNarrow', newValue.toString());
  };

  const [isHovered, setIsHovered] = useState(false);
  const environmentInfo = getEnvironmentInfo();
  const filteredSidenavItems = SidenavItems.map(item => {
    // If the item has SubItems, filter those based on showWintime and the specific URL
    if (item.SubItems) {
      // Filter out the accounting-data item if showWintime is false
      const filteredSubItems = item.SubItems.filter(sub =>
        showWintime
          ? true
          : sub.Url !== '/accounting/generateandsendaccountingdata',
      );

      // Return a new item object with filtered SubItems
      return {
        ...item,
        SubItems: filteredSubItems,
      };
    }
    // If no SubItems, return as is
    return item;
  });

  return (
    <>
      <Navbar
        bg="white"
        className="d-print-none d-xl-none flex-column position-fixed top-0 start-0 end-0 shadow"
      >
        <Container fluid>
          <Button
            variant="link"
            className="header-menu-toggler"
            onClick={() => handleTogglerClick()}
          >
            <Icon Name="list" CssClass="header-menu-toggler-icon" />
          </Button>
          <Navbar.Brand
            className="flex-grow-1 text-center mx-16 py-0"
            style={{ marginTop: -24 }}
          >
            <Link to="/" className="d-inline-block">
              <svg
                width="137"
                height="45"
                viewBox="0 0 137 45"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_1709_7632)">
                  <path
                    d="M129.947 25.7567C130.102 27.8723 131.718 28.5718 133.425 28.5718C135.302 28.5718 136.75 27.7327 136.75 26.0527C136.75 24.6535 135.718 24.0767 134.518 23.8597L133.179 23.6428C132.502 23.5183 132.302 23.2224 132.302 22.9432C132.302 22.6018 132.656 22.3058 133.395 22.3058C134.134 22.3058 134.703 22.5699 134.75 23.2392L136.705 23.224C136.552 21.1236 134.658 20.688 133.395 20.688C131.501 20.688 130.208 21.6062 130.208 23.145C130.208 24.529 131.255 25.1513 132.378 25.3531L133.948 25.6171C134.44 25.7096 134.686 25.9433 134.686 26.2393C134.686 26.6278 134.179 26.8767 133.44 26.8767C132.593 26.8767 132.024 26.5656 131.901 25.7718L129.945 25.7567H129.947ZM125.008 26.6581C123.885 26.6581 123.099 25.7096 123.099 24.6198C123.099 23.5301 123.885 22.5514 125.008 22.5514C125.639 22.5514 126.269 22.7683 126.747 23.4225V25.8021C126.269 26.4394 125.639 26.6581 125.008 26.6581ZM128.885 20.8882H126.745V21.3237C126.176 20.9504 125.484 20.7318 124.652 20.7318C122.62 20.7318 120.974 22.4269 120.974 24.6198C120.974 26.8128 122.622 28.4928 124.652 28.4928C125.482 28.4928 126.176 28.2742 126.745 27.9008V28.3364H128.885V20.8865V20.8882ZM115.974 26.2225C114.85 26.2225 114.065 25.2572 114.065 24.4029C114.065 23.2358 114.85 22.4269 115.974 22.4269C116.619 22.4269 117.235 22.6455 117.712 23.3132V25.3194C117.235 26.0039 116.619 26.2225 115.974 26.2225ZM119.836 20.8882H117.712V21.1841C117.143 20.8108 116.451 20.5939 115.619 20.5939C113.587 20.5939 111.941 22.1646 111.941 24.4046C111.941 26.3486 113.589 28.0438 115.619 28.0438C116.449 28.0438 117.143 27.8268 117.712 27.4535V28.2624C117.712 29.4295 116.974 30.0517 116.02 30.0517C115.005 30.0517 114.389 29.5388 114.296 28.7602H112.141C112.294 30.6571 113.88 31.9806 116.02 31.9806C118.16 31.9806 119.838 30.5815 119.838 28.2473V20.8898L119.836 20.8882ZM107.723 20.6544C106.892 20.6544 106.215 20.9336 105.614 21.4162V20.8882H103.474V28.3381H105.614V23.5469C106.107 22.9398 106.691 22.6136 107.23 22.6136C108.23 22.6136 108.816 23.2509 108.816 24.4332V28.3364H110.956V24.2465C110.956 22.0687 109.663 20.6544 107.724 20.6544M98.6883 22.5059C99.5803 22.5059 100.151 23.0189 100.274 23.89H96.9493C97.149 23.0962 97.7032 22.5059 98.6883 22.5059ZM102.398 24.4651C102.398 22.2402 100.828 20.7318 98.7499 20.7318C96.3952 20.7318 94.8259 22.3798 94.8259 24.6198C94.8259 26.8599 96.4118 28.5079 98.7815 28.5079C100.521 28.5079 101.907 27.5897 102.306 26.1586H100.075C99.8432 26.5319 99.4288 26.7186 98.8281 26.7186C97.7664 26.7186 97.1973 26.1435 96.9809 25.3968H102.353C102.399 25.0857 102.399 24.7746 102.399 24.4634M91.7922 20.8882H89.6522V28.3381H91.7922V23.89C92.4079 23.2055 93.2234 22.8641 94.3783 22.8171V20.7637C93.2849 20.7637 92.4545 21.1068 91.7922 21.7273V20.8882ZM84.2055 26.6581C83.0822 26.6581 82.2968 25.7096 82.2968 24.6198C82.2968 23.5301 83.0822 22.5514 84.2055 22.5514C84.8362 22.5514 85.4669 22.7683 85.9445 23.4225V25.8021C85.4669 26.4394 84.8362 26.6581 84.2055 26.6581ZM88.0829 20.8882H85.9428V21.3237C85.3737 20.9504 84.6814 20.7318 83.8494 20.7318C81.8175 20.7318 80.17 22.4269 80.17 24.6198C80.17 26.8128 81.8175 28.4928 83.8494 28.4928C84.6798 28.4928 85.3737 28.2742 85.9428 27.9008V28.3364H88.0829V20.8865V20.8882ZM75.8633 20.6544C74.7084 20.6544 73.9695 20.9806 73.1857 21.7122C72.5999 21.0277 71.7696 20.6544 70.831 20.6544C69.969 20.6544 69.4764 20.9033 69.0154 21.3388V20.8882H66.877V28.3381H69.0154V23.4544C69.4464 22.8625 69.9074 22.6153 70.3234 22.6153C71.3385 22.6153 71.9093 23.2526 71.9093 24.4349V28.3381H74.0494V24.2482C74.0494 23.969 74.0194 23.7033 73.9878 23.4393C74.4338 22.8171 74.9264 22.6153 75.3724 22.6153C76.3725 22.6153 76.9416 23.2526 76.9416 24.4349V28.3381H79.0817V24.2482C79.0817 22.0704 77.6655 20.6561 75.8649 20.6561M61.873 26.6429C60.7498 26.6429 60.0259 25.7415 60.0259 24.6215C60.0259 23.5015 60.7498 22.6623 61.873 22.6623C62.9963 22.6623 63.6886 23.5183 63.6886 24.6215C63.6886 25.7247 62.9813 26.6429 61.873 26.6429ZM61.873 20.7318C59.5949 20.7318 57.9174 22.3647 57.9174 24.6198C57.9174 26.875 59.5949 28.5702 61.873 28.5702C64.1512 28.5702 65.797 26.8901 65.797 24.6198C65.797 22.3495 64.1346 20.7318 61.873 20.7318ZM57.3017 26.3318H51.9616V17.5231H49.73V28.3381H57.3034V26.3318H57.3017Z"
                    fill="#034694"
                  />
                  <path
                    d="M38.7385 23.8513H36.9728V22.067H38.7385V23.8513ZM34.6963 11.9162L33.4482 13.1775L32.2002 11.9162L33.4482 10.655L34.6963 11.9162ZM33.4016 22.9936C33.4016 29.0679 28.5291 33.9919 22.5183 33.9919C16.5075 33.9919 11.635 29.0679 11.635 22.9936C11.635 16.9193 16.5075 11.9953 22.5183 11.9953C28.5291 11.9953 33.4016 16.9193 33.4016 22.9936ZM23.402 8.23499H21.6363V6.45071H23.402V8.23499ZM12.8398 11.9162L11.5917 13.1775L10.3436 11.9162L11.5917 10.655L12.8398 11.9162ZM10.3852 33.9616L11.6333 32.7004L12.8814 33.9616L11.6333 35.2229L10.3852 33.9616ZM21.6363 37.6261H23.402V39.4104H21.6363V37.6261ZM32.1153 33.9196L33.3634 32.6583L34.6115 33.9196L33.3634 35.1809L32.1153 33.9196ZM7.94897 23.8513H6.18334V22.067H7.94897V23.8513ZM39.9183 17.56H35.4152L38.6436 14.3009L37.8981 7.3891L31.0436 6.70969L27.8601 9.92847V5.31556L22.4983 0.959961L17.1765 5.37779V9.92847L13.9514 6.66597L7.11192 7.41937L6.43962 14.3463L9.62474 17.5634H5.06006L0.75 22.9818L5.12164 28.3599H9.62474L6.39635 31.6207L7.14187 38.5325L13.9964 39.2119L17.1798 35.9931V40.606L22.5416 44.9616L27.8635 40.5438V35.9931L31.0902 39.2556L37.9297 38.5022L38.602 31.5753L35.4169 28.3582H39.9816L44.2916 22.9398L39.92 17.5617L39.9183 17.56Z"
                    fill="#FFC20E"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1709_7632">
                    <rect
                      width="136"
                      height="44"
                      fill="white"
                      transform="translate(0.75 0.959961)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </Link>
          </Navbar.Brand>
          <div className="header-menu-placeholder" style={{ width: 90 }} />
        </Container>
        <Dropdown className="booking-area-dropdown lh-tiny" style={{ top: 50 }}>
          <Dropdown.Toggle
            variant="primary"
            size="sm"
            id="dropdown-basic"
            className="py-4 px-8 rounded-pill fs-tiny"
          >
            Varaamo: {ActiveSite ? ActiveSite.name : ''}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {SiteClaims.map(site => (
              <Dropdown.Item
                key={site.id}
                onClick={() => handleSiteClick(site.id, site.name)}
              >
                {site.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Navbar>

      {isSidenavOpen && (
        <div className="sidenav-overlay d-xl-none position-fixed top-0 bottom-0 start-0 end-0" />
      )}

      <div
        className={`sidenav ${isSidenavOpen ? 'sidenav-open' : 'sidenav-closed'}
           ${isNarrow ? 'sidenav-narrow' : ''}
              ${isHovered ? 'sidenav-hover' : ''}
            ${environmentInfo.cssClassName}
          d-print-none bg-white position-fixed top-0 bottom-0 start-0`}
      >
        <Container
          fluid
          className="sidenav-top position-relative d-flex align-items-center py-16"
        >
          <Button
            variant="link"
            className="header-menu-toggler d-xl-none px-24 py-8"
            onClick={() => handleTogglerClick()}
          >
            <Icon Name="x-lg" CssClass="header-menu-toggler-icon" />
          </Button>
          <Navbar.Brand className="d-none d-xl-block flex-grow-1 text-center ms-8">
            <Link to="/" className="d-block" style={{ width: 201 }}>
              <svg
                viewBox="0 0 201 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_1709_8136)">
                  <path
                    d="M190.929 36.0678C191.157 39.145 193.545 40.1626 196.068 40.1626C198.843 40.1626 200.982 38.942 200.982 36.4983C200.982 34.4632 199.457 33.6242 197.684 33.3086L195.704 32.9931C194.704 32.8121 194.408 32.3815 194.408 31.9755C194.408 31.4789 194.932 31.0484 196.024 31.0484C197.116 31.0484 197.957 31.4324 198.026 32.406L200.916 32.384C200.689 29.3288 197.891 28.6953 196.024 28.6953C193.226 28.6953 191.315 30.0308 191.315 32.269C191.315 34.2822 192.862 35.1872 194.522 35.4808L196.841 35.8648C197.569 35.9993 197.933 36.3393 197.933 36.7699C197.933 37.3349 197.183 37.6969 196.091 37.6969C194.839 37.6969 193.998 37.2444 193.816 36.0898L190.926 36.0678H190.929ZM183.63 37.3789C181.97 37.3789 180.809 35.9993 180.809 34.4143C180.809 32.8292 181.97 31.4055 183.63 31.4055C184.562 31.4055 185.494 31.7211 186.199 32.6726V36.1339C185.494 37.0609 184.562 37.3789 183.63 37.3789ZM189.36 28.9863H186.197V29.6199C185.356 29.0768 184.333 28.7589 183.103 28.7589C180.101 28.7589 177.668 31.2245 177.668 34.4143C177.668 37.604 180.103 40.0476 183.103 40.0476C184.33 40.0476 185.356 39.7296 186.197 39.1866V39.8202H189.36V28.9839V28.9863ZM170.278 36.7454C168.618 36.7454 167.458 35.3413 167.458 34.0987C167.458 32.4011 168.618 31.2245 170.278 31.2245C171.232 31.2245 172.142 31.5425 172.848 32.5136V35.4318C172.142 36.4274 171.232 36.7454 170.278 36.7454ZM175.986 28.9863H172.848V29.4169C172.007 28.8738 170.984 28.5583 169.754 28.5583C166.752 28.5583 164.32 30.8429 164.32 34.1012C164.32 36.9289 166.754 39.3945 169.754 39.3945C170.982 39.3945 172.007 39.079 172.848 38.5359V39.7125C172.848 41.4101 171.756 42.3152 170.347 42.3152C168.847 42.3152 167.937 41.5691 167.799 40.4366H164.615C164.841 43.1958 167.185 45.1209 170.347 45.1209C173.51 45.1209 175.989 43.0857 175.989 39.6905V28.9888L175.986 28.9863ZM158.085 28.6463C156.858 28.6463 155.857 29.0524 154.969 29.7544V28.9863H151.807V39.8226H154.969V32.8536C155.697 31.9706 156.561 31.496 157.357 31.496C158.835 31.496 159.701 32.4231 159.701 34.1427V39.8202H162.864V33.8712C162.864 30.7035 160.953 28.6463 158.088 28.6463M144.734 31.3395C146.052 31.3395 146.896 32.0856 147.078 33.3526H142.164C142.459 32.1981 143.278 31.3395 144.734 31.3395ZM150.216 34.1892C150.216 30.953 147.897 28.7589 144.825 28.7589C141.345 28.7589 139.026 31.156 139.026 34.4143C139.026 37.6725 141.37 40.0697 144.872 40.0697C147.442 40.0697 149.49 38.7341 150.08 36.6524H146.783C146.441 37.1955 145.828 37.467 144.941 37.467C143.372 37.467 142.531 36.6304 142.211 35.5444H150.149C150.218 35.0918 150.218 34.6393 150.218 34.1868M134.543 28.9863H131.38V39.8226H134.543V33.3526C135.453 32.3571 136.658 31.8605 138.365 31.792V28.8053C136.749 28.8053 135.522 29.3043 134.543 30.2069V28.9863ZM123.331 37.3789C121.671 37.3789 120.51 35.9993 120.51 34.4143C120.51 32.8292 121.671 31.4055 123.331 31.4055C124.263 31.4055 125.195 31.7211 125.901 32.6726V36.1339C125.195 37.0609 124.263 37.3789 123.331 37.3789ZM129.061 28.9863H125.899V29.6199C125.058 29.0768 124.035 28.7589 122.805 28.7589C119.802 28.7589 117.367 31.2245 117.367 34.4143C117.367 37.604 119.802 40.0476 122.805 40.0476C124.032 40.0476 125.058 39.7296 125.899 39.1866V39.8202H129.061V28.9839V28.9863ZM111.003 28.6463C109.296 28.6463 108.204 29.1209 107.046 30.1849C106.18 29.1894 104.953 28.6463 103.566 28.6463C102.292 28.6463 101.564 29.0084 100.883 29.6419V28.9863H97.723V39.8226H100.883V32.7191C101.52 31.8581 102.201 31.4985 102.816 31.4985C104.316 31.4985 105.16 32.4256 105.16 34.1452V39.8226H108.322V33.8737C108.322 33.4676 108.278 33.0811 108.231 32.6971C108.89 31.792 109.618 31.4985 110.277 31.4985C111.755 31.4985 112.597 32.4256 112.597 34.1452V39.8226H115.759V33.8737C115.759 30.706 113.666 28.6488 111.005 28.6488M90.328 37.3569C88.668 37.3569 87.5982 36.0458 87.5982 34.4167C87.5982 32.7876 88.668 31.567 90.328 31.567C91.988 31.567 93.011 32.8121 93.011 34.4167C93.011 36.0213 91.9658 37.3569 90.328 37.3569ZM90.328 28.7589C86.9613 28.7589 84.4823 31.134 84.4823 34.4143C84.4823 37.6945 86.9613 40.1602 90.328 40.1602C93.6947 40.1602 96.1269 37.7165 96.1269 34.4143C96.1269 31.112 93.6701 28.7589 90.328 28.7589ZM83.5724 36.9044H75.6807V24.0917H72.3828V39.8226H83.5749V36.9044H83.5724Z"
                    fill="#034694"
                  />
                  <path
                    d="M56.1398 33.2964H53.5306V30.7011H56.1398V33.2964ZM50.1663 15.9364L48.3219 17.771L46.4774 15.9364L48.3219 14.1018L50.1663 15.9364ZM48.253 32.0489C48.253 40.8843 41.0523 48.0465 32.1695 48.0465C23.2867 48.0465 16.086 40.8843 16.086 32.0489C16.086 23.2136 23.2867 16.0514 32.1695 16.0514C41.0523 16.0514 48.253 23.2136 48.253 32.0489ZM33.4753 10.5819H30.8661V7.98655H33.4753V10.5819ZM17.8665 15.9364L16.022 17.771L14.1776 15.9364L16.022 14.1018L17.8665 15.9364ZM14.2391 48.0024L16.0835 46.1679L17.928 48.0024L16.0835 49.837L14.2391 48.0024ZM30.8661 53.3325H33.4753V55.9278H30.8661V53.3325ZM46.352 47.9413L48.1964 46.1067L50.0409 47.9413L48.1964 49.7759L46.352 47.9413ZM10.6387 33.2964H8.02946V30.7011H10.6387V33.2964ZM57.8834 24.1455H51.2287L55.9996 19.405L54.8979 9.35147L44.7682 8.36325L40.0637 13.0451V6.33542L32.14 0L24.2753 6.42593V13.0451L19.5093 8.29965L9.40172 9.3955L8.40819 19.471L13.1152 24.1504H6.36946L0 32.0318L6.46046 39.8545H13.1152L8.34424 44.5975L9.44599 54.651L19.5757 55.6392L24.2802 50.9573V57.667L32.2039 64.0024L40.0686 57.5765V50.9573L44.8371 55.7028L54.9446 54.6069L55.9382 44.5314L51.2312 39.852H57.9769L64.3464 31.9706L57.8859 24.148L57.8834 24.1455Z"
                    fill="#FFC20E"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1709_8136">
                    <rect width="200.982" height="64" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </Link>
          </Navbar.Brand>
          <div
            className="header-menu-placeholder d-xl-none"
            style={{ width: 74 }}
          />
          <Dropdown
            className="booking-area-dropdown lh-tiny d-none d-xl-block"
            style={{ top: 64, left: 85 }}
          >
            <Dropdown.Toggle
              variant="primary"
              size="sm"
              id="dropdown-basic"
              className="py-4 px-8 rounded-pill fs-tiny"
            >
              {t('sidenav:site')}: {ActiveSite ? ActiveSite.name : ''}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {SiteClaims.map(site => (
                <Dropdown.Item
                  key={site.id}
                  onClick={() => handleSiteClick(site.id, site.name)}
                >
                  {site.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Button
            variant="link"
            onClick={toggleSidebarWidth}
            className="d-none d-md-block shadow p-0 px-4 position-absolute rounded-circle bg-white start-100 top-50 translate-middle"
          >
            <span
              className={`bi ${isNarrow ? 'bi-chevron-right' : 'bi-chevron-left'}`}
            />
          </Button>
        </Container>
        <Nav
          className="sidenav-navigation flex-column flex-nowrap position-absolute start-0 end-0 py-16"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <SidenavItem
            ActivePath={location.pathname}
            HandleCloseSubmenu={handleCloseSubmenu}
            Item={DashboardItem}
          />
          {filteredSidenavItems.map(item => (
            <SidenavItem
              ActivePath={location.pathname}
              key={encodeURI(item.Name)}
              HandleCloseSubmenu={handleCloseSubmenu}
              Item={item}
            />
          ))}
        </Nav>

        <div className="sidenav-bottom align-items-center bg-white border-top d-flex justify-content-between position-absolute bottom-0 start-0 end-0 p-24">
          <span className="sidenav-environment-label text-uppercase">
            {environmentInfo.buildLabel} | {environmentInfo.environmentLabel}
          </span>

          <div className="d-flex align-items-center ms-16">
            <OverlayTrigger
              key="tooltip-userName"
              placement="top"
              overlay={<Tooltip id="tooltip-userName">{Username}</Tooltip>}
            >
              <span
                className="d-inline-block ratio ratio-1x1 rounded-circle bg-primary small"
                style={{ width: 40 }}
              >
                <span className="position-absolute w-auto h-auto top-50 start-50 translate-middle">
                  {Initials}
                </span>
              </span>
            </OverlayTrigger>
            {/*
            *** User settings hidden since there is no feature
            <OverlayTrigger
              key="tooltip-userSettings"
              placement="top"
              overlay={
                <Tooltip id="tooltip-userSettings">
                  {t('sidenav:settings')}
                </Tooltip>
              }
            >
              <a
                href="!#"
                className="navbar-user-link fs-4 ms-16 me-8 mt-4"
                role="button"
              >
                <Icon Name="gear" />
              </a>
            </OverlayTrigger>
            */}
            <OverlayTrigger
              key="tooltip-userLogout"
              placement="top"
              overlay={
                <Tooltip id="tooltip-userLogout">{t('sidenav:logout')}</Tooltip>
              }
            >
              <a
                className="navbar-user-link fs-4 ms-16 me-8"
                href={LogoutUrl}
                role="button"
              >
                <Icon Name="unlock" />
              </a>
            </OverlayTrigger>
            {!isTravelAgencyOnlyUser && (
              <div className="language-switcher">
                <Button
                  className="p-8"
                  variant="link"
                  onClick={() =>
                    i18n.language === 'fi'
                      ? changeLanguage(LOCALES.En)
                      : changeLanguage(LOCALES.Fi)
                  }
                >
                  <img
                    className="d-inline-block rounded-circle"
                    src={i18n.language === 'fi' ? FlagEn : FlagFi}
                    alt=""
                    height={32}
                    width={32}
                  />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidenav;
export { Sidenav };
